import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { apiEndpoint } from "../../utils/env";

function getAuthToken() {
  try {
    return (
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token") ||
      null
    );
  } catch {
    return null;
  }
}

export function sendToServer(level, message, meta) {
  try {
    const token = getAuthToken();
    if (!token) return; // not logged in — skip
    fetch(apiEndpoint("/api/Admin/LogClientEntry"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        level,
        message: String(message ?? ""),
        meta: meta ? JSON.stringify(meta) : null,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {}); // fire-and-forget, ignore network errors
  } catch {}
}

const LogsContext = createContext(null);

export function LogsProvider({ children }) {
  const [entries, setEntries] = useState(() => {
    try {
      const raw = localStorage.getItem("app_logs");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("app_logs", JSON.stringify(entries.slice(-500)));
    } catch {}
  }, [entries]);

  // Deduplicate bursts of identical messages by merging with the last entry if same level+message within 5s
  // Keep a ref of entries so log() can synchronously return the created/updated id.
  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  const log = useCallback((level, message, meta) => {
    const now = Date.now();
    const msg = String(message ?? "");
    const last = entriesRef.current[entriesRef.current.length - 1];
    if (
      last &&
      last.level === level &&
      last.message === msg &&
      now - new Date(last.ts).getTime() < 5000
    ) {
      // merge with last
      setEntries((prev) => {
        const next = [...prev];
        const count = (last.meta && last.meta.count) || 1;
        next[next.length - 1] = {
          ...last,
          meta: {
            ...(last.meta || {}),
            count: count + 1,
            lastTs: new Date(now).toISOString(),
          },
        };
        return next;
      });
      return last.id;
    }

    const id = now + ":" + Math.random().toString(36).slice(2);
    const item = {
      id,
      ts: new Date(now).toISOString(),
      level,
      message: msg,
      meta: meta ?? null,
    };
    setEntries((prev) => [...prev, item].slice(-500));
    sendToServer(level, msg, meta);
    return id;
  }, []);

  const clear = useCallback(() => setEntries([]), []);
  const remove = useCallback(
    (id) => setEntries((prev) => prev.filter((e) => e.id !== id)),
    []
  );

  const api = useMemo(
    () => ({ entries, log, clear, remove }),
    [entries, log, clear, remove]
  );

  return <LogsContext.Provider value={api}>{children}</LogsContext.Provider>;
}

export function useLogs() {
  const ctx = useContext(LogsContext);
  if (!ctx) throw new Error("useLogs must be used within LogsProvider");
  return ctx;
}
